import { useRef, useState } from 'react'
import { submitContactSubmission } from '@/api/cmsService'
import { cmsContactField, cmsTextOrFallback } from '@/api/helpers'
import AuditCtaButton from '@/components/ui/AuditCtaButton'
import { cmsClamp, cmsEllipsis, cmsParagraph, cmsTextTitle } from '@/styles/cmsText'

import './Contact.css'

const DEFAULT_CONTACT_TITLE = 'Ready to See What Control Looks Like?'
const DEFAULT_CONTACT_DESCRIPTION = 'Get Your Free Operational Audit'

interface ContactProps {
    isMobile: boolean
    cmsReady?: boolean
    contactInfo?: {
        title?: string
        description?: string
        email?: string
        phone?: string
        address?: string
        cta?: string
    }
}

interface FormData {
    name: string
    company: string
    email: string
    message: string
}

interface FormStatus {
    loading: boolean
    success: boolean
    error: string | null
}

interface FieldErrors {
    name: string | null
    company: string | null
    email: string | null
    message: string | null
}

const ERROR_LABEL_STYLE: React.CSSProperties = {
    position: 'absolute',
    bottom: '8px',
    left: '20px',
    fontFamily: "'Instrument Sans', sans-serif",
    fontSize: '13px',
    lineHeight: '16px',
    color: '#FF6B6B',
    pointerEvents: 'none',
}

const CONTACT_ICON_CIRCLE: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '12px',
    width: '56px',
    height: '56px',
    minWidth: '56px',
    background: '#1C4F73',
    borderRadius: '32px',
    flexShrink: 0,
}

const MOBILE_CONTACT_VALUE_TEXT: React.CSSProperties = {
    color: '#C9C6C5',
    fontSize: '18px',
    lineHeight: '24px',
    fontFamily: "'Instrument Sans', sans-serif",
    fontWeight: 600,
    flex: '1 1 auto',
    minWidth: 0,
    ...cmsClamp(2),
}

const CONTACT_SECTION_CONTENT: React.CSSProperties = {
    position: 'absolute',
    width: '100%',
    height: '100%',
    top: 0,
    left: 0,
    zIndex: 10,
    overflow: 'hidden',
    boxSizing: 'border-box',
}

const CONTACT_DESKTOP_TITLE: React.CSSProperties = {
    position: 'absolute',
    top: '72px',
    left: '50%',
    transform: 'translateX(-50%)',
    width: 'min(1009px, calc(100% - 48px))',
    height: '59px',
    fontFamily: "'Instrument Sans', sans-serif",
    fontStyle: 'normal',
    fontWeight: 700,
    fontSize: '48px',
    lineHeight: '59px',
    textAlign: 'center',
    color: '#F4F0EF',
    margin: 0,
    padding: 0,
    boxSizing: 'border-box',
    ...cmsEllipsis(),
}

const CONTACT_DESKTOP_DESCRIPTION: React.CSSProperties = {
    position: 'absolute',
    top: '151px',
    left: '50%',
    transform: 'translateX(-50%)',
    width: 'min(686px, calc(100% - 48px))',
    fontFamily: "'Instrument Sans', sans-serif",
    fontStyle: 'normal',
    fontWeight: 400,
    fontSize: '32px',
    lineHeight: '39px',
    textAlign: 'center',
    color: '#C9C6C5',
    margin: 0,
    padding: 0,
    boxSizing: 'border-box',
    ...cmsParagraph(),
}

const CONTACT_MOBILE_HEADER: React.CSSProperties = {
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    width: '100%',
    maxWidth: '360px',
    overflow: 'hidden',
    boxSizing: 'border-box',
}

export default function Contact({ isMobile, cmsReady = true, contactInfo }: ContactProps) {
    const sectionRef = useRef<HTMLDivElement>(null)
    const fieldErrorTimeoutRef = useRef<NodeJS.Timeout | null>(null)
    const contactTitle = cmsTextOrFallback(contactInfo?.title, DEFAULT_CONTACT_TITLE, cmsReady)
    const contactDescription = cmsTextOrFallback(
        contactInfo?.description,
        DEFAULT_CONTACT_DESCRIPTION,
        cmsReady
    )
    const email = cmsContactField(contactInfo?.email, 'info@digitrek.ae', cmsReady)
    const phone = cmsContactField(contactInfo?.phone, '+971 50 1234567', cmsReady)
    const address = cmsContactField(contactInfo?.address, 'Dubai-United Arab Emirates', cmsReady)
    const submitLabel = 'Book Your Free Consultation'
    const phoneHref = `tel:${phone.replace(/\s+/g, '')}`
    const [formData, setFormData] = useState<FormData>({
        name: '',
        company: '',
        email: '',
        message: ''
    })
    const [formStatus, setFormStatus] = useState<FormStatus>({
        loading: false,
        success: false,
        error: null
    })
    const [fieldErrors, setFieldErrors] = useState<FieldErrors>({
        name: null,
        company: null,
        email: null,
        message: null,
    })

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target
        setFormData(prev => ({ ...prev, [name]: value }))
        if (fieldErrors[name as keyof FieldErrors]) {
            setFieldErrors(prev => ({ ...prev, [name]: null }))
        }
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

        const errors: FieldErrors = {
            name: !formData.name.trim() ? 'Full name is required' : null,
            company: !formData.company.trim() ? 'Company name is required' : null,
            email: !formData.email.trim()
                ? 'Email address is required'
                : !emailRegex.test(formData.email)
                ? 'Please enter a valid email address'
                : null,
            message: !formData.message.trim() ? 'Please describe your challenge' : null,
        }

        const hasErrors = Object.values(errors).some(Boolean)
        if (hasErrors) {
            setFieldErrors(errors)
            if (fieldErrorTimeoutRef.current) clearTimeout(fieldErrorTimeoutRef.current)
            fieldErrorTimeoutRef.current = setTimeout(() => {
                setFieldErrors({ name: null, company: null, email: null, message: null })
            }, 5000)
            return
        }

        setFormStatus({ loading: true, success: false, error: null })

        try {
            const result = await submitContactSubmission({
                fullName: formData.name,
                companyName: formData.company,
                companyEmail: formData.email,
                operationalChallenge: formData.message,
            })

            if (!result.ok) {
                setFormStatus({
                    loading: false,
                    success: false,
                    error: result.message,
                })
                return
            }

            setFormStatus({ loading: false, success: true, error: null })
            setFormData({ name: '', company: '', email: '', message: '' })

            setTimeout(() => {
                setFormStatus(prev => ({ ...prev, success: false }))
            }, 5000)
        } catch (error) {
            setFormStatus({
                loading: false,
                success: false,
                error: 'Failed to send message. Please try again later.'
            })
        }
    }

    if (isMobile) {
        return (
            <section
                id="contact"
                ref={sectionRef}
                aria-label="Contact us"
                style={{
                    position: 'relative',
                    width: '100%',
                    padding: '60px 16px',
                    background: '#191A1A',
                    overflow: 'hidden',
                }}
            >
                {/* Background Images */}
                <div
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundImage: 'url(/images/contact-bg.png)',
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        zIndex: 0,
                    }}
                />
                <div
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundImage: 'url(/images/noisy-gradients.png)',
                        backgroundSize: 'cover',
                        mixBlendMode: 'overlay',
                        opacity: 0.6,
                        zIndex: 1,
                        pointerEvents: 'none',
                    }}
                />
                <div
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        background: 'linear-gradient(180deg, rgba(14, 29, 39, 0) -75.89%, #07141D 57.98%, #000000 100%)',
                        zIndex: 2,
                        mixBlendMode: 'multiply',
                        pointerEvents: 'none',
                    }}
                />


                <div style={{ position: 'relative', zIndex: 10, display: 'flex', flexDirection: 'column', gap: '40px', alignItems: 'center' }}>
                    {/* Header */}
                    <div style={CONTACT_MOBILE_HEADER}>
                        <h2
                            title={cmsTextTitle(contactTitle)}
                            style={{
                                width: '100%',
                                fontFamily: "'Instrument Sans', sans-serif",
                                fontWeight: 700,
                                fontSize: '28px',
                                lineHeight: '36px',
                                color: '#F4F0EF',
                                margin: 0,
                                ...cmsClamp(3),
                            }}
                        >
                            {contactTitle}
                        </h2>
                        {contactDescription ? (
                        <p
                            title={cmsTextTitle(contactDescription)}
                            style={{
                                width: '100%',
                                fontFamily: "'Instrument Sans', sans-serif",
                                fontWeight: 400,
                                fontSize: '18px',
                                lineHeight: '28px',
                                color: '#C9C6C5',
                                margin: 0,
                                ...cmsParagraph(),
                            }}
                        >
                            {contactDescription}
                        </p>
                        ) : (
                        <p
                            style={{
                                fontFamily: "'Instrument Sans', sans-serif",
                                fontWeight: 400,
                                fontSize: '18px',
                                lineHeight: '28px',
                                color: '#C9C6C5',
                                margin: 0,
                            }}
                        >
                            Get Your Free Operational Audit
                        </p>
                        )}
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '24px', width: '100%', maxWidth: '100%' }}>
                        {/* Full Name */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <label style={{ fontFamily: "'Instrument Sans', sans-serif", fontWeight: 600, fontSize: '16px', color: '#F4F0EF' }}>
                                Full Name <span style={{ color: '#D32F2F' }}>*</span>
                            </label>
                            <div style={{ position: 'relative' }}>
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    disabled={formStatus.loading || formStatus.success}
                                    style={{
                                        width: '100%',
                                        height: '56px',
                                        background: 'rgba(255, 255, 255, 0.02)',
                                        border: `1px solid ${fieldErrors.name ? '#FF6B6B' : '#ADAAAA'}`,
                                        backdropFilter: 'blur(11.05px)',
                                        borderRadius: '12px',
                                        padding: '0 16px',
                                        color: 'white',
                                        fontSize: '16px',
                                        fontFamily: "'Instrument Sans', sans-serif",
                                    }}
                                />
                                {fieldErrors.name && <span style={ERROR_LABEL_STYLE}>{fieldErrors.name}</span>}
                            </div>
                        </div>

                        {/* Company Name */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <label style={{ fontFamily: "'Instrument Sans', sans-serif", fontWeight: 600, fontSize: '16px', color: '#F4F0EF' }}>
                                Company Name <span style={{ color: '#D32F2F' }}>*</span>
                            </label>
                            <div style={{ position: 'relative' }}>
                                <input
                                    type="text"
                                    name="company"
                                    value={formData.company}
                                    onChange={handleInputChange}
                                    disabled={formStatus.loading || formStatus.success}
                                    style={{
                                        width: '100%',
                                        height: '56px',
                                        background: 'rgba(255, 255, 255, 0.02)',
                                        border: `1px solid ${fieldErrors.company ? '#FF6B6B' : '#ADAAAA'}`,
                                        backdropFilter: 'blur(11.05px)',
                                        borderRadius: '12px',
                                        padding: '0 16px',
                                        color: 'white',
                                        fontSize: '16px',
                                        fontFamily: "'Instrument Sans', sans-serif",
                                    }}
                                />
                                {fieldErrors.company && <span style={ERROR_LABEL_STYLE}>{fieldErrors.company}</span>}
                            </div>
                        </div>

                        {/* Company Email */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <label style={{ fontFamily: "'Instrument Sans', sans-serif", fontWeight: 600, fontSize: '16px', color: '#F4F0EF' }}>
                                Company Email <span style={{ color: '#D32F2F' }}>*</span>
                            </label>
                            <div style={{ position: 'relative' }}>
                                <input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    disabled={formStatus.loading || formStatus.success}
                                    style={{
                                        width: '100%',
                                        height: '56px',
                                        background: 'rgba(255, 255, 255, 0.02)',
                                        border: `1px solid ${fieldErrors.email ? '#FF6B6B' : '#ADAAAA'}`,
                                        backdropFilter: 'blur(11.05px)',
                                        borderRadius: '12px',
                                        padding: '0 16px',
                                        color: 'white',
                                        fontSize: '16px',
                                        fontFamily: "'Instrument Sans', sans-serif",
                                    }}
                                />
                                {fieldErrors.email && <span style={ERROR_LABEL_STYLE}>{fieldErrors.email}</span>}
                            </div>
                        </div>

                        {/* Challenge */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <label style={{ fontFamily: "'Instrument Sans', sans-serif", fontWeight: 600, fontSize: '16px', color: '#F4F0EF' }}>
                                What is your biggest operational challenge? <span style={{ color: '#D32F2F' }}>*</span>
                            </label>
                            <div style={{ position: 'relative' }}>
                                <textarea
                                    name="message"
                                    value={formData.message}
                                    onChange={handleInputChange}
                                    disabled={formStatus.loading || formStatus.success}
                                    style={{
                                        width: '100%',
                                        height: '140px',
                                        background: 'rgba(255, 255, 255, 0.02)',
                                        border: `1px solid ${fieldErrors.message ? '#FF6B6B' : '#ADAAAA'}`,
                                        backdropFilter: 'blur(11.05px)',
                                        borderRadius: '12px',
                                        padding: '16px',
                                        color: 'white',
                                        fontSize: '16px',
                                        resize: 'none',
                                        fontFamily: "'Instrument Sans', sans-serif",
                                    }}
                                />
                                {fieldErrors.message && <span style={ERROR_LABEL_STYLE}>{fieldErrors.message}</span>}
                            </div>
                        </div>

                        {/* Network error */}
                        {formStatus.error && (
                            <div
                                title={cmsTextTitle(formStatus.error)}
                                style={{
                                    padding: '12px',
                                    background: 'rgba(211, 47, 47, 0.1)',
                                    border: '1px solid #D32F2F',
                                    borderRadius: '8px',
                                    color: '#FF6B6B',
                                    fontSize: '14px',
                                    fontFamily: "'Instrument Sans', sans-serif",
                                    textAlign: 'center',
                                    width: '100%',
                                    boxSizing: 'border-box',
                                    ...cmsClamp(3),
                                }}
                            >
                                {formStatus.error}
                            </div>
                        )}

                        {/* Submit Button */}
                        <AuditCtaButton
                            as="button"
                            variant="default"
                            compact
                            className="audit-cta-button--form"
                            loading={formStatus.loading}
                            success={formStatus.success}
                            title={cmsTextTitle(
                                formStatus.success
                                    ? 'Message sent successfully'
                                    : formStatus.loading
                                      ? 'Sending...'
                                      : submitLabel
                            )}
                            style={{
                                marginTop: '16px',
                                alignSelf: 'center',
                            }}
                        >
                            {formStatus.success
                                ? 'Message sent successfully'
                                : formStatus.loading
                                  ? 'Sending...'
                                  : submitLabel}
                        </AuditCtaButton>
                    </form>

                    {/* Contact Info Mobile */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', alignItems: 'stretch', width: '100%', maxWidth: '360px', margin: '24px auto 0', padding: '0 16px', boxSizing: 'border-box' }}>
                        {/* Email */}
                        <a href={`mailto:${email}`} style={{ display: 'flex', alignItems: 'center', gap: '12px', minHeight: '40px', textDecoration: 'none', cursor: 'pointer' }}>
                            <div style={{ width: '40px', height: '40px', background: '#1C4F73', borderRadius: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z" stroke="#F4F0EF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M22 6L12 13L2 6" stroke="#F4F0EF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </div>
                            <span style={MOBILE_CONTACT_VALUE_TEXT} title={cmsTextTitle(email)}>
                                {email}
                            </span>
                        </a>
                        
                        {/* Phone */}
                        <a href={phoneHref} style={{ display: 'flex', alignItems: 'center', gap: '12px', minHeight: '40px', textDecoration: 'none', cursor: 'pointer' }}>
                            <div style={{ width: '40px', height: '40px', background: '#1C4F73', borderRadius: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M22 16.92V19.92C22.0011 20.1986 21.9441 20.4742 21.8325 20.7294C21.7209 20.9846 21.5573 21.2137 21.3521 21.402C21.1468 21.5902 20.9046 21.7336 20.6407 21.8228C20.3769 21.912 20.0974 21.9452 19.82 21.92C16.7428 21.5857 13.787 20.5342 11.19 18.84C8.77382 17.2281 6.72913 15.151 5.15002 12.69C3.49753 10.054 2.48496 7.05835 2.19002 3.94002C2.1654 3.66442 2.19808 3.38676 2.28599 3.12513C2.3739 2.86349 2.51515 2.62369 2.70055 2.42123C2.88596 2.21877 3.11136 2.05814 3.36224 1.94971C3.61313 1.84128 3.88383 1.78754 4.15702 1.79202H7.23002C7.71216 1.78764 8.17838 1.95669 8.54471 2.26876C8.91104 2.58083 9.155 3.01655 9.23302 3.49802C9.37877 4.60416 9.65171 5.68882 10.04 6.72002C10.1952 7.12608 10.1931 7.57444 10.034 7.97902C9.87493 8.3836 9.56942 8.71802 9.17602 8.91802L7.90002 9.56002C9.32452 12.115 11.4045 14.168 13.97 15.57L14.65 14.33C14.8569 13.9431 15.195 13.6429 15.6026 13.4839C16.0102 13.3249 16.4601 13.3175 16.871 13.463C17.9271 13.8293 19.0384 14.0827 20.17 14.21C20.6695 14.281 21.1251 14.5298 21.4552 14.9016C21.7853 15.2734 21.9634 15.7381 21.95 16.23V16.92Z" stroke="#F4F0EF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </div>
                            <span style={MOBILE_CONTACT_VALUE_TEXT} title={cmsTextTitle(phone)}>
                                {phone}
                            </span>
                        </a>
                        
                        {/* Location */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minHeight: '40px' }}>
                            <div style={{ width: '40px', height: '40px', background: '#1C4F73', borderRadius: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M21 10C21 17 12 23 12 23C12 23 3 17 3 10C3 7.61305 3.94821 5.32387 5.63604 3.63604C7.32387 1.94821 9.61305 1 12 1C14.3869 1 16.6761 1.94821 18.364 3.63604C20.0518 5.32387 21 7.61305 21 10Z" stroke="#F4F0EF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M12 13C13.6569 13 15 11.6569 15 10C15 8.34315 13.6569 7 12 7C10.3431 7 9 8.34315 9 10C9 11.6569 10.3431 13 12 13Z" stroke="#F4F0EF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </div>
                            <span style={MOBILE_CONTACT_VALUE_TEXT} title={cmsTextTitle(address)}>
                                {address}
                            </span>
                        </div>
                    </div>

                </div>
            </section>
        )
    }

    // Desktop Version
    return (
        <section
            id="contact"
            ref={sectionRef}
            aria-label="Contact us"
            style={{
                position: 'relative',
                width: '1920px',
                height: '1068px',
                left: '50%',
                transform: 'translateX(-50%)',
                margin: '0 auto',
                top: '0px',
                background: '#191A1A',
                overflow: 'hidden',
            }}
        >
            {/* martin-martz-R_9BV4WRphI-unsplash 1 */}
            <div
                style={{
                    position: 'absolute',
                    width: '1920px',
                    height: '1073px',
                    left: '0px',
                    top: '-5px',
                    backgroundImage: 'url(/images/contact-bg.png)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    zIndex: 0,
                }}
            />

            {/* noisy-gradients */}
            <div
                style={{
                    position: 'absolute',
                    width: '1920px',
                    height: '1073px',
                    left: '0px',
                    top: '-5px',
                    backgroundImage: 'url(/images/noisy-gradients.png)',
                    backgroundSize: 'cover',
                    mixBlendMode: 'overlay',
                    zIndex: 1,
                    pointerEvents: 'none',
                }}
            />

            {/* Rectangle 67 - Gradient Overlay */}
            <div
                style={{
                    position: 'absolute',
                    width: '1917px',
                    height: '1068px',
                    left: '3px',
                    top: '0px',
                    background: 'linear-gradient(180deg, rgba(14, 29, 39, 0) -75.89%, #07141D 57.98%, #000000 100%)',
                    zIndex: 2,
                    pointerEvents: 'none',
                }}
            />

            {/* Content Container - zIndex > 2 */}
            <div style={CONTACT_SECTION_CONTENT}>

                {/* Heading */}
                <h2
                    className="cms-ellipsis"
                    title={cmsTextTitle(contactTitle)}
                    style={CONTACT_DESKTOP_TITLE}
                >
                    {contactTitle}
                </h2>

                <p
                    title={cmsTextTitle(contactDescription)}
                    style={CONTACT_DESKTOP_DESCRIPTION}
                >
                    {contactDescription}
                </p>

                {/* Form Group */}
                <div
                    style={{
                        position: 'absolute',
                        width: '828px',
                        maxWidth: 'calc(100% - 48px)',
                        height: '701px',
                        left: 'calc(50% - 828px/2)',
                        top: '259px',
                        overflow: 'hidden',
                        boxSizing: 'border-box',
                    }}
                >
                    <form onSubmit={handleSubmit}>
                        {/* Full Name */}
                        <div style={{
                            position: 'absolute',
                            width: '828px',
                            left: '0px',
                            top: '0px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '12px',
                        }}>
                            <label style={{
                                fontFamily: "'Instrument Sans', sans-serif",
                                fontWeight: 600,
                                fontSize: '24px',
                                lineHeight: '29px',
                                color: '#F4F0EF',
                            }}>
                                Full Name <span style={{ color: '#D32F2F' }}>*</span>
                            </label>
                            <div style={{ position: 'relative' }}>
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    disabled={formStatus.loading || formStatus.success}
                                    style={{
                                        width: '828px',
                                        height: '64px',
                                        background: 'rgba(255, 255, 255, 0.02)',
                                        border: `1px solid ${fieldErrors.name ? '#FF6B6B' : '#ADAAAA'}`,
                                        backdropFilter: 'blur(11.05px)',
                                        borderRadius: '12px',
                                        color: 'white',
                                        fontSize: '20px',
                                        padding: '0 20px',
                                        fontFamily: "'Instrument Sans', sans-serif",
                                    }}
                                />
                                {fieldErrors.name && <span style={ERROR_LABEL_STYLE}>{fieldErrors.name}</span>}
                            </div>
                        </div>

                        {/* Company Name */}
                        <div style={{
                            position: 'absolute',
                            width: '828px',
                            left: '0px',
                            top: '133px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '12px',
                        }}>
                            <label style={{
                                fontFamily: "'Instrument Sans', sans-serif",
                                fontWeight: 600,
                                fontSize: '24px',
                                lineHeight: '29px',
                                color: '#F4F0EF',
                            }}>
                                Company Name <span style={{ color: '#D32F2F' }}>*</span>
                            </label>
                            <div style={{ position: 'relative' }}>
                                <input
                                    type="text"
                                    name="company"
                                    value={formData.company}
                                    onChange={handleInputChange}
                                    disabled={formStatus.loading || formStatus.success}
                                    style={{
                                        width: '828px',
                                        height: '64px',
                                        background: 'rgba(255, 255, 255, 0.02)',
                                        border: `1px solid ${fieldErrors.company ? '#FF6B6B' : '#ADAAAA'}`,
                                        backdropFilter: 'blur(11.05px)',
                                        borderRadius: '12px',
                                        color: 'white',
                                        fontSize: '20px',
                                        padding: '0 20px',
                                        fontFamily: "'Instrument Sans', sans-serif",
                                    }}
                                />
                                {fieldErrors.company && <span style={ERROR_LABEL_STYLE}>{fieldErrors.company}</span>}
                            </div>
                        </div>

                        {/* Company Email */}
                        <div style={{
                            position: 'absolute',
                            width: '828px',
                            left: '0px',
                            top: '266px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '12px',
                        }}>
                            <label style={{
                                fontFamily: "'Instrument Sans', sans-serif",
                                fontWeight: 600,
                                fontSize: '24px',
                                lineHeight: '29px',
                                color: '#F4F0EF',
                            }}>
                                Company Email <span style={{ color: '#D32F2F' }}>*</span>
                            </label>
                            <div style={{ position: 'relative' }}>
                                <input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    disabled={formStatus.loading || formStatus.success}
                                    style={{
                                        width: '828px',
                                        height: '64px',
                                        background: 'rgba(255, 255, 255, 0.02)',
                                        border: `1px solid ${fieldErrors.email ? '#FF6B6B' : '#ADAAAA'}`,
                                        backdropFilter: 'blur(11.05px)',
                                        borderRadius: '12px',
                                        color: 'white',
                                        fontSize: '20px',
                                        padding: '0 20px',
                                        fontFamily: "'Instrument Sans', sans-serif",
                                    }}
                                />
                                {fieldErrors.email && <span style={ERROR_LABEL_STYLE}>{fieldErrors.email}</span>}
                            </div>
                        </div>

                        {/* Challenge */}
                        <div style={{
                            position: 'absolute',
                            width: '828px',
                            left: '0px',
                            top: '399px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '12px',
                        }}>
                            <label style={{
                                fontFamily: "'Instrument Sans', sans-serif",
                                fontWeight: 600,
                                fontSize: '24px',
                                lineHeight: '29px',
                                color: '#F4F0EF',
                            }}>
                                What is your biggest operational challenge? <span style={{ color: '#D32F2F' }}>*</span>
                            </label>
                            <div style={{ position: 'relative' }}>
                                <textarea
                                    name="message"
                                    value={formData.message}
                                    onChange={handleInputChange}
                                    disabled={formStatus.loading || formStatus.success}
                                    style={{
                                        width: '828px',
                                        height: '172px',
                                        background: 'rgba(255, 255, 255, 0.02)',
                                        border: `1px solid ${fieldErrors.message ? '#FF6B6B' : '#ADAAAA'}`,
                                        backdropFilter: 'blur(11.05px)',
                                        borderRadius: '12px',
                                        color: 'white',
                                        fontSize: '20px',
                                        padding: '20px',
                                        resize: 'none',
                                        fontFamily: "'Instrument Sans', sans-serif",
                                    }}
                                />
                                {fieldErrors.message && <span style={ERROR_LABEL_STYLE}>{fieldErrors.message}</span>}
                            </div>
                        </div>

                        {/* Network error */}
                        {formStatus.error && (
                            <div
                                title={cmsTextTitle(formStatus.error)}
                                style={{
                                    position: 'absolute',
                                    width: '828px',
                                    maxWidth: '100%',
                                    left: '0px',
                                    top: '590px',
                                    padding: '12px',
                                    background: 'rgba(211, 47, 47, 0.1)',
                                    border: '1px solid #D32F2F',
                                    borderRadius: '8px',
                                    color: '#FF6B6B',
                                    fontSize: '16px',
                                    fontFamily: "'Instrument Sans', sans-serif",
                                    textAlign: 'center',
                                    boxSizing: 'border-box',
                                    ...cmsClamp(2),
                                }}
                            >
                                {formStatus.error}
                            </div>
                        )}

                        {/* Submit Button */}
                        <AuditCtaButton
                            as="button"
                            variant="default"
                            className="audit-cta-button--form"
                            loading={formStatus.loading}
                            success={formStatus.success}
                            title={cmsTextTitle(
                                formStatus.success
                                    ? 'Message sent successfully'
                                    : formStatus.loading
                                      ? 'Sending...'
                                      : submitLabel
                            )}
                            style={{
                                position: 'absolute',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                top: '640px',
                                ...(formStatus.loading || formStatus.success
                                    ? {}
                                    : { maxWidth: 'calc(100% - 48px)' }),
                            }}
                        >
                            {formStatus.success
                                ? 'Message sent successfully'
                                : formStatus.loading
                                  ? 'Sending...'
                                  : submitLabel}
                        </AuditCtaButton>

                    </form>
                </div>

                {/* Footer Info - Frame 119 */}
                <div className="contact-footer-row">
                    {email ? (
                        <a
                            href={`mailto:${email}`}
                            className="contact-footer-item contact-footer-item--email"
                        >
                            <div style={CONTACT_ICON_CIRCLE}>
                                <img src="/images/SVGs/mail.svg" alt="" style={{ width: '32px', height: '32px' }} />
                            </div>
                            <span className="contact-footer-item__text" title={cmsTextTitle(email)}>
                                {email}
                            </span>
                        </a>
                    ) : null}

                    {phone ? (
                        <a href={phoneHref} className="contact-footer-item contact-footer-item--phone">
                            <div style={CONTACT_ICON_CIRCLE}>
                                <img src="/images/SVGs/phone.svg" alt="" style={{ width: '32px', height: '32px' }} />
                            </div>
                            <span className="contact-footer-item__text" title={cmsTextTitle(phone)}>
                                {phone}
                            </span>
                        </a>
                    ) : null}

                    {address ? (
                        <div className="contact-footer-item contact-footer-item--address">
                            <div style={CONTACT_ICON_CIRCLE}>
                                <img src="/images/SVGs/map-pin.svg" alt="" style={{ width: '32px', height: '32px' }} />
                            </div>
                            <span className="contact-footer-item__text" title={cmsTextTitle(address)}>
                                {address}
                            </span>
                        </div>
                    ) : null}
                </div>

            </div>
        </section>
    )
}
